/**
 * @fileoverview Error context and provider for global error state management
 * Provides centralized error handling with React context and hooks
 */

import React, { createContext, useContext, useReducer, useCallback, ReactNode, useRef, useEffect } from 'react';
import { ApiError, ErrorCode } from '../services/api/errors';

// Error state interface
export interface ErrorState {
  errors: ApiError[];
  hasErrors: boolean;
}

// Error actions
type ErrorAction =
  | { type: 'ADD_ERROR'; payload: ApiError }
  | { type: 'REMOVE_ERROR'; payload: string } // Remove by timestamp
  | { type: 'CLEAR_ALL_ERRORS' }
  | { type: 'CLEAR_ERRORS_BY_CODE'; payload: ErrorCode };

// Error context interface
interface ErrorContextType {
  state: ErrorState;
  addError: (error: ApiError) => void;
  removeError: (timestamp: string) => void;
  clearAllErrors: () => void;
  clearErrorsByCode: (code: ErrorCode) => void;
  hasErrorsByCode: (code: ErrorCode) => boolean;
  getErrorsByCode: (code: ErrorCode) => ApiError[];
  getErrorsBySeverity: (severity: 'low' | 'medium' | 'high' | 'critical') => ApiError[];
}

// Initial state
const initialState: ErrorState = {
  errors: [],
  hasErrors: false,
};

// Error reducer
function errorReducer(state: ErrorState, action: ErrorAction): ErrorState {
  switch (action.type) {
    case 'ADD_ERROR':
      const newErrors = [...state.errors, action.payload];
      return {
        errors: newErrors,
        hasErrors: newErrors.length > 0,
      };

    case 'REMOVE_ERROR':
      const filteredErrors = state.errors.filter(
        error => error.timestamp !== action.payload
      );
      return {
        errors: filteredErrors,
        hasErrors: filteredErrors.length > 0,
      };

    case 'CLEAR_ALL_ERRORS':
      return initialState;

    case 'CLEAR_ERRORS_BY_CODE':
      const remainingErrors = state.errors.filter(
        error => error.code !== action.payload
      );
      return {
        errors: remainingErrors,
        hasErrors: remainingErrors.length > 0,
      };

    default:
      return state;
  }
}

// Create context
const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

// Error provider props
interface ErrorProviderProps {
  children: ReactNode;
  maxErrors?: number; // Maximum number of errors to keep in state
  autoRemoveAfter?: number; // Auto-remove errors after X milliseconds
}

// Error provider component
export function ErrorProvider({ 
  children, 
  maxErrors = 10,
  autoRemoveAfter = 5000 // 5 seconds default
}: ErrorProviderProps) {
  const [state, dispatch] = useReducer(errorReducer, initialState);
  const errorsRef = useRef<ApiError[]>([]);

  // Keep errorsRef in sync with state
  useEffect(() => {
    errorsRef.current = state.errors;
  }, [state.errors]);

  // Add error with auto-removal
  const addError = useCallback((error: ApiError) => {
    dispatch({ type: 'ADD_ERROR', payload: error });

    // Auto-remove after specified time for non-critical errors
    if (error.getSeverity() !== 'critical' && autoRemoveAfter > 0) {
      setTimeout(() => {
        dispatch({ type: 'REMOVE_ERROR', payload: error.timestamp });
      }, autoRemoveAfter);
    }

    // Enforce max errors limit
    if (errorsRef.current.length >= maxErrors) {
      // Remove oldest error
      const oldestError = errorsRef.current[0];
      if (oldestError) {
        setTimeout(() => {
          dispatch({ type: 'REMOVE_ERROR', payload: oldestError.timestamp });
        }, 100);
      }
    }
  }, [autoRemoveAfter, maxErrors]);

  // Remove specific error
  const removeError = useCallback((timestamp: string) => {
    dispatch({ type: 'REMOVE_ERROR', payload: timestamp });
  }, []);

  // Clear all errors
  const clearAllErrors = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL_ERRORS' });
  }, []);

  // Clear errors by code
  const clearErrorsByCode = useCallback((code: ErrorCode) => {
    dispatch({ type: 'CLEAR_ERRORS_BY_CODE', payload: code });
  }, []);

  // Check if has errors by code
  const hasErrorsByCode = useCallback((code: ErrorCode) => {
    return state.errors.some(error => error.code === code);
  }, [state.errors]);

  // Get errors by code
  const getErrorsByCode = useCallback((code: ErrorCode) => {
    return state.errors.filter(error => error.code === code);
  }, [state.errors]);

  // Get errors by severity
  const getErrorsBySeverity = useCallback((severity: 'low' | 'medium' | 'high' | 'critical') => {
    return state.errors.filter(error => error.getSeverity() === severity);
  }, [state.errors]);

  const value: ErrorContextType = {
    state,
    addError,
    removeError,
    clearAllErrors,
    clearErrorsByCode,
    hasErrorsByCode,
    getErrorsByCode,
    getErrorsBySeverity,
  };

  return (
    <ErrorContext.Provider value={value}>
      {children}
    </ErrorContext.Provider>
  );
}

// Hook to use error context
export function useErrorContext(): ErrorContextType {
  const context = useContext(ErrorContext);
  if (context === undefined) {
    throw new Error('useErrorContext must be used within an ErrorProvider');
  }
  return context;
}

// Convenience hooks
export function useErrors() {
  const { state } = useErrorContext();
  return state.errors;
}

export function useHasErrors() {
  const { state } = useErrorContext();
  return state.hasErrors;
}

export function useAddError() {
  const { addError } = useErrorContext();
  return addError;
}

export function useClearErrors() {
  const { clearAllErrors } = useErrorContext();
  return clearAllErrors;
}
